import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { Card, Container, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';

// components
import Iconify from '../../../components/iconify';
import AssetHeader from './AssetHeader';
import ActionsPopover from './ActionsPopover';
import DeleteModal from './DeleteModal';
import AssetFormModal from './AssetFormModal';
import AssetBulkUploadModal from './AssetBulkUploadModal';
import Loading from '../../../layouts/loading';

// styles
import 'react-toastify/dist/ReactToastify.css';

import { canManageAssets } from '../../../utils/share';
import {
  boldHeader,
  ASSET_STATUS_OPTIONS,
  ASSET_LIST_TABLE_COLUMNS,
  ASSET_LIST_TABLE_DEFAULT_PROPS,
} from '../constants/assetConstants';
import BreadCrumb from '../../../components/breadcrumb/BreadCrumbNavigation';
import TableWrapper from '../../../common/TableWrapper';
import {
  createAsset,
  deleteAsset,
  getAllAssets,
  getAllFilteredAssets,
  getUsers,
  updateAsset,
} from '../../../services/assetService';

// ----------------------------------------------------------------------

const canEditAsset = canManageAssets();
const canDeleteAsset = canManageAssets();

// ----------------------------------------------------------------------

// Map a built-in column filter id to the query param the backend expects.
// The "Assigned To" column displays the user name but filters by user id.
const COLUMN_TO_PARAM = {
  status: 'status',
  type: 'type',
  category: 'category',
  assignedUserName: 'assignedTo',
};

function AssetTable() {
  const navigate = useNavigate();
  const userId = useSelector((state) => state?.login?.user.id || null);
  const [openActions, setOpenActions] = useState(null);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [openBulkUpload, setOpenBulkUpload] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [action, setAction] = useState('');
  const [selectedRow, setSelectedRow] = useState([]);
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);

  const fetchData = async () => {
    try {
      setIsLoading(true);

      const sortBy = sorting[0]?.id || 'createdAt';
      const sortOrder = sorting[0]?.desc ? 'DESC' : 'ASC';
      const page = pagination.pageIndex + 1;
      const limit = pagination.pageSize;

      // Translate the table's built-in column filters into backend query params.
      const filterParams = columnFilters.reduce((acc, { id, value }) => {
        const param = COLUMN_TO_PARAM[id];
        if (param && value !== undefined && value !== null && value !== '') {
          acc[param] = value;
        }
        return acc;
      }, {});

      const res = await getAllFilteredAssets({
        keyword: globalFilter,
        sortBy,
        sortOrder,
        page,
        limit,
        ...filterParams,
      });

      setAssets(res.data.data.rows);
      setRowCount(res.data.data.total);
    } catch (error) {
      toast.error('Error loading assets');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const users=async()=>{
      const user = await getUsers();
      setUsers(user?.data?.data);
    }
    users();
  }, []);
  useEffect(() => {
    // Debounce text-driven searches (global search + the free-text Type/Category
    // column filters) so we don't fire a request on every keystroke. Select-based
    // filters, sorting and pagination fetch immediately.
    const hasTextSearch =
      Boolean(globalFilter) || columnFilters.some((f) => f.id === 'type' || f.id === 'category');
    const timeout = setTimeout(() => {
      fetchData();
    }, hasTextSearch ? 500 : 0);

    return () => clearTimeout(timeout);
  }, [globalFilter, columnFilters, sorting, pagination]);

  const handleViewClick = useCallback(() => {
    setOpenActions(null);
    navigate(`/assets/${selectedRow.original.id}`);
  }, [navigate, selectedRow]);

  const handleBulkUploadSuccess = useCallback(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateClick = () => {
    setAction('create');
    setOpenFormModal(true);
  };

  const handleFormModalClose = useCallback(() => {
    setOpenFormModal(false);
  }, []);

  const handleOpenMenu = (event, row) => {
    // Don't let the click bubble up to the row (which navigates to view mode).
    event.stopPropagation();
    setOpenActions(event.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setOpenActions(null);
  };

  const handleEditClick = useCallback(() => {
    setAction('edit');
    setOpenFormModal(true);
    setOpenActions(null);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setOpenDelete(true);
    setOpenActions(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setOpenDelete(false);
  }, []);

  const handleDelete = useCallback(async () => {
    try {
      setIsLoading(true);
      await deleteAsset(selectedRow.original.id);
      toast.success('Asset deleted successfully');
      setOpenDelete(false);

      // Refresh local list
      await fetchData();
    } catch (error) {
      console.error('Error deleting asset:', error);
      toast.error('Error deleting asset');
    } finally {
      setIsLoading(false);
    }
  }, [selectedRow]);

  const handleFormModalSubmit = useCallback(
    async (formData) => {
      try {
        setIsLoading(true);
        if (action === 'create') {
          await createAsset(formData);
        } else {
          await updateAsset(selectedRow.original.id, formData);
        }
        toast.success(`Asset ${action === 'create' ? 'created' : 'updated'} successfully`);
        setOpenFormModal(false);

        // Refresh local list
        await fetchData();
      } catch (error) {
        console.error(`Error ${action === 'create' ? 'creating' : 'updating'} asset:`, error);
        toast.error(`Error ${action === 'create' ? 'creating' : 'updating'} asset`);
      } finally {
        setIsLoading(false);
      }
    },
    [action, selectedRow]
  );

  // ----------------------------------------------------------------------
  // ✅ Table setup: client-side pagination, sorting, and search
  // ----------------------------------------------------------------------
  const columns = useMemo(() => {
    // The backend only supports filtering by status, type, category and assignedTo.
    // Wire those built-in column filters; disable the rest (use the global search box).
    const userOptions = (users || []).map((user) => ({
      value: user.id.toString(),
      label: user.userName,
    }));

    const withFilters = ASSET_LIST_TABLE_COLUMNS.map((col) => {
      switch (col.accessorKey) {
        case 'status':
          return { ...col, enableColumnFilter: true, filterVariant: 'select', filterSelectOptions: ASSET_STATUS_OPTIONS };
        case 'assignedUserName':
          return { ...col, filterVariant: 'select', filterSelectOptions: userOptions };
        case 'type':
        case 'category':
          return col; // default free-text filter, mapped to the backend
        default:
          return { ...col, enableColumnFilter: false };
      }
    });

    return [
      ...withFilters,
      {
        id: 'actions',
        header: 'Actions',
        Header: boldHeader('#454F5B'),
        enableColumnFilter: false,
        enableSorting: false,
        size: 50,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            onClick={(event) => handleOpenMenu(event, row)}
            data-testid="action-buttons-container"
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        ),
      },
    ];
  }, [users]);

  const table = useMaterialReactTable({
    columns,
    data: assets,
    rowCount,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    manualSorting: true,
    manualPagination: true,
    enablePagination: true,
    enableSorting: true,
    enableGlobalFilter: true,
    enableColumnFilters: true,
    globalFilterFn: 'contains',
    state: {
      globalFilter,
      columnFilters,
      sorting,
      pagination,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => navigate(`/assets/${row.original.id}`),
      sx: { backgroundColor: '#fff', cursor: 'pointer' },
    }),
    ...ASSET_LIST_TABLE_DEFAULT_PROPS,
    muiPaginationProps: {
      color: 'primary',
      variant: 'outlined',
      shape: 'rounded',
    },
  });

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Assets', path: '' },
  ];

  return (
    <>
      <BreadCrumb breadcrumbs={breadcrumbs} />
      <Container maxWidth="4xl">
        <Loading open={isLoading} />

        <AssetHeader
          onCreateClick={handleCreateClick}
          onDashboardClick={() => navigate('/assets/dashboard')}
          onBulkUploadClick={() => setOpenBulkUpload(true)}
        />

        <Card>
          <TableWrapper>
            <MaterialReactTable table={table} />
          </TableWrapper>
        </Card>
      </Container>

      <ActionsPopover
        open={openActions}
        onClose={handleCloseMenu}
        onViewClick={handleViewClick}
        onEditClick={handleEditClick}
        onDeleteClick={handleDeleteClick}
        canEdit={canEditAsset}
        canDelete={canDeleteAsset}
      />

      {openFormModal && (
        <AssetFormModal
          open
          onClose={handleFormModalClose}
          selectedRow={selectedRow}
          action={action}
          onSubmit={handleFormModalSubmit}
          users={users}
        />
      )}

      <DeleteModal open={openDelete} onClose={handleDeleteModalClose} onDelete={handleDelete} />

      {openBulkUpload && (
        <AssetBulkUploadModal
          open
          onClose={() => setOpenBulkUpload(false)}
          onSuccess={handleBulkUploadSuccess}
        />
      )}
    </>
  );
}

export default AssetTable;
